import React, { useState, useEffect, useCallback, useRef } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useLoading } from "../../../context/LoadingContext";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import dayjs from "dayjs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import {
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  Switch,
  Button,
  FormControlLabel,
} from '@mui/material';
import {
  fetchData,
  searchfetchQueries,
} from "../../Requirement/services/RequirementServices";
import AlertModal from "../../Requirement/components/common/AlerModel";
import RequirementForm from "./RequirementForm";
import MatchingOffers from "./component/MatchingOffers";

import RequirementDetails from "../../global-partner/buyer-activity/requirement/component/RequirementDetails";
import "bootstrap/dist/css/bootstrap.min.css";
import * as bootstrap from "bootstrap"; // Import Bootstrap JS
import { styled } from '@mui/material/styles';
const Requirements = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [activeUser, setActiveUser] = useState("");
  const [buyerInfo, setBuyerInfo] = useState([]);
  const [mode, setMode] = useState("Open");
  const [requirementList, setRequirementList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [searchValues, setSearchValues] = useState({
    requirementNumber: "",
    searchProduct: "",
    searchProductGrade: "",
    searchQuantityRange: "",
  });
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [formMode, setFormMode] = useState(null);
  const [productList, setProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);
  const [allProductMaster, setAllProductMaster] = useState([]);
  const [matchingOffers, setMatchingOffers] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [requirementHistory, setRequirementHistory] = useState([]);
  const [matchingOffersRequirement, setMatchingOffersRequirement] =
    useState(null);
  const modalRef = useRef(null); // Reference to the modal DOM element

  const quantityOptions = [
    { value: "", label: "Select Range" },
    { value: "50-100", label: "50-100 MT" },
    { value: "100-200", label: "100-200 MT" },
    { value: "200-500", label: "200-500 MT" },
    { value: "Above-500", label: "Above 500 MT" },
  ];

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setMatchingOffers(false);
  };

  const getBuyerInfo = useCallback(async (buyerEmail) => {
    try {
      const response = await fetchData({
        target: "listAMTPLUsers",
        userOrder: "AMTPLUserID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { EmailID: buyerEmail },
      });
      setBuyerInfo(response.items || []);
    } catch (error) {
      console.error("Error loading buyer info:", error);
      setBuyerInfo([]);
    }
  }, []);

  const loadAllProducts = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductID",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);
      const totalCount = initialResponse?.total || initialResponse?.length || 0;
      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductID",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        setAllProductMaster(allProducts);
      } else {
        setAllProductMaster([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: 1,
          filterTerms: { Status: "ACTIVE" },
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: totalCount,
            filterTerms: { Status: "ACTIVE" },
          }),
        ]);

        const distinctProducts = Array.from(
          new Map(allProducts.map((p) => [p.ProductName, p])).values()
        );
        setProductList(distinctProducts);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductGrades = useCallback(
    async (productName) => {
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName },
          }),
        ]);
        if (allGrades.items.length > 0) {
          setProductGradeList(allGrades.items);
        } else {
          setProductGradeList([]);
        }
      } catch (error) {
        console.error("Error loading product grades: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const searchRequirements = useCallback(
    async (searchValue, newRowStart = rowStart) => {
      setLoading(true);
      try {
        let quantityFilter = {};
        if (searchValue.searchQuantityRange) {
          if (searchValue.searchQuantityRange === "Above-500") {
            quantityFilter = { QuantityGTE: 500 };
          } else {
            const [min, max] = searchValue.searchQuantityRange
              .split("-")
              .map((val) => parseFloat(val));
            quantityFilter = { QuantityGTE: min, QuantityLTE: max };
          }
        }

        const today = new Date().toISOString().split("T")[0];
        let productId = "";
        if (searchValue.searchProduct && searchValue.searchProductGrade) {
          const product = allProductMaster.find(
            (p) =>
              p.ProductName === searchValue.searchProduct &&
              p.Grade === searchValue.searchProductGrade
          );
          if (product) {
            productId = product.ProductID;
          } else {
            console.warn(
              `No product found for ProductName: ${searchValue.searchProduct}, Grade: ${searchValue.searchProductGrade}`
            );
          }
        }

        const body = {
          target: "listRequirements",
          userOrder: "CreateDate DESC",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            CategoryType: "requirement",
            ...(mode === "Close"
              ? { ValidUntilLT: "'" + today + "'" }
              : { ValidUntilGTE: "'" + today + "'" }),
            BuyerEmailID: activeUser,
            ...(searchValue.requirementNumber && {
              RequirementNumber: searchValue.requirementNumber,
            }),
            ProductID: productId,
            ...quantityFilter,
          },
        };
        const response = await fetchData(body);

        const modifiedItems = (response.items || response).map((item) => ({
          ...item,
          id: item.RequirementsID,
        }));

        setRequirementList(modifiedItems);
        setTotalItems(response.total || 0);
        setIsFormVisible(false)
      } catch (error) {
        console.error("Error loading requirements: ", error);
        setRequirementList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, setLoading, activeUser, mode, allProductMaster]
  );

  const handleSearch = (searchValues) => {
    setRowStart(0);
    searchRequirements(searchValues, 0);
    setMatchingOffers(false);
  };

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    searchRequirements(searchValues, newRowStart);
  };

  const handleAdd = () => {
    setIsFormVisible((prev) => !prev);
    setFormMode("add");
    setSelectedRequirement(null);
    setMatchingOffers(false);
  };

  const handleActionClick = (event, row, mode) => {
    console.log(row, "rowvalue")
    event.stopPropagation();
    setSelectedRequirement(row);
    setFormMode(mode);
    setIsFormVisible(true);
    setMatchingOffers(false);
    setHistoryVisible(false);
  };

  const handleRowClick = (row, event) => {
    console.log("ROW@@@@",row)
    setSelectedRequirement(row); // Set the selected requirement
    setMatchingOffers(true);
    setMatchingOffersRequirement(row);
    setIsFormVisible(false);
    setHistoryVisible(false);
  };

  const handleHistoryClick = async () => {
    if (!selectedRequirement) {
      alert("Please select a requirement first.");
      return;
    }

    setHistoryVisible(true);
    setLoading(true);

    try {
      // Fetch history from ModuleLog service
      const body = {
        target: "listModuleLog",
        filterTerms: {
          Module: "REQUIREMENT",
          Id: selectedRequirement.RequirementsID,
        },
        userOrder: "CreatedAt DESC",
        rowStart: 0,
        pageSize: 50,
      };
      const historyData = await fetchData(body);

      if (historyData && historyData.items) {
        // Transform the data to match the timeline format
        const transformedHistory = historyData.items.map((item, index) => ({
          id: index + 1,
          title: item.Info || "N/A",
          company: item.SubInfo || "",
          datetime: item.CreatedAt
            ? dayjs(item.CreatedAt).format("DD MMM YYYY")
            : "",
          status: "completed",
        }));
        setRequirementHistory(transformedHistory);
      } else {
        setRequirementHistory([]);
      }
    } catch (error) {
      console.error("Error fetching requirement history:", error);
      setRequirementHistory([]);
    } finally {
      setLoading(false);
    }

    if (modalRef.current) {
      const bsModal = new bootstrap.Modal(modalRef.current, {
        backdrop: true,
        keyboard: true,
      });
      bsModal.show();
    }
  };

  const cancelHistory = () => {
    setHistoryVisible(false);
    // Programmatically hide the Bootstrap modal
    if (modalRef.current) {
      const bsModal = bootstrap.Modal.getInstance(modalRef.current);
      if (bsModal) {
        bsModal.hide();
      }
    }
  };

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  const fields = [
    {
      name: "requirementNumber",
      label: "Requirement Number",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementNumber: value })),
      value: searchValues.requirementNumber,
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: productList.map((item) => ({
        value: item.ProductName,
        label: item.ProductName,
      })),
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProduct: value }));
        loadProductGrades(value);
      },
    },
    {
      name: "searchProductGrade",
      label: "Product Grade",
      type: "dropdown",
      options: productGradeList.map((item) => ({
        value: item.Grade,
        label: item.Grade,
      })),
      value: searchValues.searchProductGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProductGrade: value }));
      },
    },
    {
      name: "searchQuantityRange",
      label: "Quantity Range (MT)",
      type: "dropdown",
      options: quantityOptions,
      value: searchValues.searchQuantityRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchQuantityRange: value }));
        searchRequirements({ ...searchValues, searchQuantityRange: value }, 0);
      },
    },
  ];

  const columns = [
    { id: "RequirementNumber", numeric: false, label: "Req No." },
    {
      id: "Product",
      numeric: false,
      label: "Product",
      renderCell: (row) => {
        const pn = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.ProductName : "";
      },
    },
    {
      id: "Grade",
      numeric: false,
      label: "Grade",
      renderCell: (row) => {
        const pn = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.Grade : "";
      },
    },
    { id: "Quantity", numeric: true, label: "Quantity(MT)" },
    { id: "OriginCountry", numeric: false, label: "Origin" },
    {
      id: "DeliveryLocation",
      numeric: false,
      label: "Destination",
      renderCell: (row) => {
        let res = row.DeliveryLocation.split("~");
        if (Array.isArray(res)) {
          return res[0];
        }
        return "";
      },
    },
    {
      id: "ShipmentPeriod",
      numeric: false,
      label: "Shipment Period",
      renderCell: (row) => {
        let dpFrom = row.DeliveryPeriodFrom
          ? dayjs(row.DeliveryPeriodFrom).format("DD-MM-YYYY")
          : "";
        let dpTill = row.DeliveryPeriodTill
          ? dayjs(row.DeliveryPeriodTill).format("DD-MM-YYYY")
          : "";
        return dpFrom !== "" && dpTill !== "" ? dpFrom + " to " + dpTill : "";
      },
    },
    {
      id: "DeliveryPort",
      numeric: false,
      label: "Delivery Port",
      renderCell: (row) => {
        let res = row.DeliveryLocation.split("~");
        if (Array.isArray(res) && res.length > 0) {
          return res[1];
        }
        return "";
      },
    },
    { id: "Incoterm", numeric: false, label: "Incoterms" },
    {
      id: "ProductPackaging",
      numeric: false,
      label: "Packaging",
      renderCell: (row) => {
        const jsonLike = row.ProductPackaging.replace(/{|}/g, "")
          .split(",")
          .map((part) => part.split("="))
          .reduce((obj, [key, value]) => {
            obj[key.trim()] = value ? value.trim() : "";
            return obj;
          }, {});
        return jsonLike.item;
      },
    },
    {
      id: "RequirementDate",
      numeric: false,
      label: "Created",
      renderCell: (row) =>
        row.RequirementDate
          ? dayjs(row.RequirementDate).format("DD-MM-YYYY")
          : "",
    },
    {
      id: "ValidUntil",
      numeric: false,
      label: "Expires",
      renderCell: (row) =>
        row.ValidUntil ? dayjs(row.ValidUntil).format("DD-MM-YYYY") : "",
    },
    {
      id: "action",
      numeric: false,
      label: "Action",
      renderCell: (row) => (
        <div
          className="edit-icon"
          style={{
            display: "flex",
            gap: "2px",
            alignItems: "center",
            margin: 0,
            padding: 0,
          }}
        >
          <Tooltip title="Edit Requirement">
            <IconButton
              onClick={(e) => handleActionClick(e, row, "edit")}
              sx={{ color: "#de6b14" }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_BUYERS")) {
          await Auth.signOut();
          navigate("/login/buyer");
        } else {
          setActiveUser(user.attributes.email);
          getBuyerInfo(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/buyer");
      }
    };
    checkUser();
    loadProductList();
    loadAllProducts();
  }, [navigate, loadProductList, loadAllProducts, getBuyerInfo]);

  useEffect(() => {
    if (activeUser && allProductMaster.length > 0) {
      searchRequirements(searchValues);
    }
  }, [
    rowStart,
    pageSize,
    activeUser,
    mode,
    searchRequirements,
    searchValues,
    allProductMaster,
  ]);

  const selectedRows = matchingOffersRequirement
    ? [matchingOffersRequirement.RequirementsID || matchingOffersRequirement.id]
    : [];
  const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    display: 'flex',
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transition: 'transform 300ms ease-in-out',
      '&.Mui-checked': {
        transform: 'translateX(16px) !important',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#de6b14',
          opacity: 1,
          border: 0,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#de6b14',
        border: '6px solid #fff',
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
      transition: 'all 300ms ease-in-out !important',
    },
    '& .MuiSwitch-track': {
      borderRadius: 13,
      backgroundColor: '#ccc',
      opacity: 1,
      transition: 'background-color 300ms ease-in-out !important',
    },
  }));
  return (
    <div>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />

      <div className="table-header-re">
        <div className="head-btn">
          {mode}
          <FormControlLabel
            control={
              <IOSSwitch
                checked={mode === "Open"}
                onChange={(e) => handleModeChange(e.target.checked ? "Open" : "Close")}
                name="mode"
                sx={{ m: 1 }}
              />
            }
            label=""
            sx={{ marginRight: 1 }}
          />
          <button className="submit-btn" onClick={handleAdd}>
            {isFormVisible ? "Cancel" : "Add"}
          </button>
        </div>
      </div>

      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">My Requirements</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={requirementList}
        total={totalItems}
        title="Requirement Information"
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        currentPage={currentPage}
        onRowClick={handleRowClick}
        selectedRows={selectedRows}
      />

      {matchingOffersRequirement &&
        matchingOffers &&
        matchingOffersRequirement.RequirementsID && (
          <>
            <div className="table-header">
              <div className="header-text">
                <div className="head-text">
                  Requirement details:{" "}
                  {matchingOffersRequirement.RequirementNumber || ""}
                </div>
              </div>
              <div className="head-btn">
                <button
                  className="submit-btn-more"
                  onClick={handleHistoryClick}
                >
                  History
                </button>
              </div>
            </div>
            <RequirementDetails
              reqid={true}
              selectedOffer={matchingOffersRequirement}
              allProductMaster={allProductMaster}
            />
          </>
        )}

      {isFormVisible && (
        <RequirementForm
          formMode={formMode}
          setFormMode={setFormMode}
          selectedRequirement={selectedRequirement}
          setIsFormVisible={setIsFormVisible}
          productList={productList}
          allProductMaster={allProductMaster}
          activeUser={activeUser}
          buyerInfo={buyerInfo}
          totalItems={totalItems}
          setModal={setModal}
          searchRequirements={searchRequirements}
          searchValues={searchValues}
          navigate={navigate}
        />
      )}

      {matchingOffers && matchingOffersRequirement && (
        <MatchingOffers
          requirementDetails={matchingOffersRequirement}
          activeUser={activeUser}
        />
      )}

      <div
        className="modal modal-right fade"
        id="historyModal"
        tabIndex="-1"
        aria-labelledby="historyModalLabel"
        aria-hidden="true"
        ref={modalRef}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="historyModalLabel">
                History
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={cancelHistory}
              ></button>
            </div>
            <div className="modal-body">
              <ul className="timeline list-unstyled position-relative ps-4">
                {requirementHistory.length > 0 ? (
                  requirementHistory.map((item) => (
                    <li key={item.id} className="mb-1 position-relative">
                      <span
                        className="timeline-icon rounded-circle d-inline-block position-absolute"
                        style={{
                          width: "14px",
                          height: "14px",
                          left: "-28px",
                          top: "5px",
                          backgroundColor: "rgb(222 107 20)",
                        }}
                      ></span>
                      <div>
                        <strong>{item.title}</strong>
                        {item.company && (
                          <div className="text-muted small">{item.company}</div>
                        )}
                        <div className="text-muted small">{item.datetime}</div>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="mb-4">
                    <div className="text-muted">No history available</div>
                  </li>
                )}
              </ul>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={cancelHistory}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Requirements;
